import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN
import { Radio, Tag, Empty, Button, Divider, Tooltip } from "antd";
// I18N
import { useTranslation } from "react-i18next";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { updateBankSelected, updateLiftSelected } from "../../../redux/monitoring/monitoringSlice";
// FLAT BUFFERS
import { Control } from "../../../flatbuffers/generated/control/control";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
import GETJwtToken from "../../../redux/authentication/authentication";
// RTK QUERY
import { useLazyGetActionBanksByBankQuery } from "../../../redux/actions/actionBankAPI";
// INTERFACES
import {
  BankInterface,
  BankFloorInterface,
  BankActionInterface,
} from "../../../interfaces/Bank.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { RootState } from "../../../app/store";
// UTILS — adapter protocol detection mirrors StepActions.isMceAdapter
import { isMceAdapter } from "../../setupWizard/steps/StepActions";
// LOCAL — shared with TerminalGenericCommand
import FloorButtonGrid from "./FloorButtonGrid";
import useFrontRearDoorAvailability from "./useFrontRearDoorAvailability";
import { groupActionsByType, hasAnyActions, ACTION_PANEL_ORDER } from "./actionBankGrouping";
import {
  COMMAND_SPECS,
  liftHasDoor,
  bankHasDoorSide,
  CommandKind,
  DoorSide,
} from "./terminalCommandSpecs";

/**
 * Resolve a Control enum value from a cmd string like "EnergizeCarCall".
 * The action_catalog.cmd field stores the Control enum NAME — convert
 * to the numeric value so usePostCommand can send it on the wire.
 * Returns null when the name is not in the Control enum (forward-compat
 * for newly-added Control variants the bundle doesn't know about yet).
 */
function controlValueFromCmd(cmd: string | undefined | null): number | null {
  if (!cmd) return null;
  const v = (Control as unknown as Record<string, number>)[cmd];
  return typeof v === "number" ? v : null;
}

/**
 * Section title row for the dynamic-actions panel. Same styling
 * pattern the existing Tag chips use to keep the visual rhythm of the
 * Terminal page consistent.
 */
function ActionTypeSectionHeader({ type, count }: { type: string; count: number }) {
  const labelMap: Record<string, string> = {
    TOGGLE: "Toggle",
    HOLDBACK: "Holdback",
    TURNBACK: "Turnback",
    TOGGLETIMER: "Toggle Timer",
    ENERGIZER_TURN_BACK: "Energizer Turnback",
    SWITCH: "Switch",
  };
  return (
    <div style={{ marginBottom: 8, marginTop: 12 }}>
      <Tag color="geekblue">{labelMap[type] ?? type}</Tag>
      <span style={{ fontSize: 12, opacity: 0.7, marginLeft: 6 }}>
        {count} action{count === 1 ? "" : "s"}
      </span>
    </div>
  );
}

/**
 * MCE bank command panel for the Terminal.
 *
 * Renders one row per lift with floor buttons. Front/Rear is gated per
 * lift+floor against the lift's per-floor door config (with bank-level
 * fallback). Only renders when the bank is backed by an MCE adapter.
 *
 * When the bank has action_bank rows configured (auto-seeded by
 * SeedBankControlActions or set via the wizard), a "Dynamic Actions"
 * section renders above the MCE block, grouping each row by its
 * buttonType (Toggle / Holdback / Turnback / ToggleTimer /
 * EnergizerTurnback / Switch). Click on a row sends the linked
 * action_catalog.cmd via the existing usePostCommand hook.
 */
export default function TerminalBankCommands({ bank }: { bank: BankInterface }) {
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const { theme } = useSelector((state: RootState) => state.home);

  const [commandKind, setCommandKind] = useState<CommandKind>("ENERGIZE_CAR_CALL");
  const { hasFront, hasRear, door, setDoor } = useFrontRearDoorAvailability(bank);

  const { triggerPostCommand, isLoadingPostCommand } = usePostCommand();

  const isMce = useMemo(() => isMceAdapter((bank as any).adapter), [bank]);

  // Dynamic-actions fetch — pulls action_bank rows configured for this
  // bank (auto-seeded by the SeedBankControlActions reconciler or set
  // by the wizard's Visual Config modal). When zero rows come back we
  // fall through to the MCE preset block below; when any are present
  // we render them as the primary action surface, grouped by type.
  const [triggerGetActionBanks, { data: actionBankData }] = useLazyGetActionBanksByBankQuery();
  useEffect(() => {
    const bankId = String((bank as any).id ?? "");
    if (!bankId) return;
    (async () => {
      const token = await GETJwtToken();
      triggerGetActionBanks({ bankId, token });
    })();
  }, [(bank as any).id, triggerGetActionBanks]);

  const actionBankRows = useMemo<BankActionInterface[]>(() => {
    const raw = (actionBankData as any)?.data ?? actionBankData;
    return Array.isArray(raw) ? (raw as BankActionInterface[]) : [];
  }, [actionBankData]);
  const actionGroups = useMemo(() => groupActionsByType(actionBankRows), [actionBankRows]);
  const dynamicActionsAvailable = useMemo(() => hasAnyActions(actionGroups), [actionGroups]);

  /**
   * Send the action_bank's primary command. Uses the linked action
   * catalog row (preloaded server-side as `action.cmd`) to resolve the
   * Control enum value, then dispatches via the same usePostCommand
   * path as the MCE preset block so service-mode + WS routing stay
   * uniform. No-ops with a console.warn when the cmd doesn't resolve
   * to a known Control variant — happens for newly-added backend
   * commands the bundle doesn't know about yet.
   */
  const sendActionBankCommand = (row: BankActionInterface, active: boolean) => {
    // `action` is the preloaded action_catalog row attached by the
    // backend's Preload chain (see action-bank.controller.go). It rides
    // on BankActionInterface as an optional camelCase field — we
    // declare it inline rather than widening the interface globally
    // since this is the only consumer that needs the preload shape.
    const action = (row as BankActionInterface & { action?: { cmd?: string } }).action;
    const cmd = action?.cmd ?? "";
    const controlVal = controlValueFromCmd(cmd);
    if (controlVal === null) {
      // eslint-disable-next-line no-console
      console.warn(
        `[TerminalBankCommands] Unknown Control cmd for action_bank ${row.id}: "${cmd}"`,
      );
      return;
    }
    dispatch(updateBankSelected(bank as any));
    triggerPostCommand({
      floor: 0,
      command: controlVal,
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active,
    });
  };

  const sortedFloors = useMemo<BankFloorInterface[]>(() => {
    const floors = ((bank.bankFloors ?? []) as BankFloorInterface[]).slice();
    return floors.sort((a, b) => b.floorIndex - a.floorIndex);
  }, [bank.bankFloors]);

  const lifts = useMemo<LiftInterface[]>(
    () => ((bank.lifts ?? []) as LiftInterface[]).slice().sort((a, b) => a.position - b.position),
    [bank.lifts],
  );

  const spec = useMemo(
    () => COMMAND_SPECS.find((s) => s.key === commandKind) ?? COMMAND_SPECS[0],
    [commandKind],
  );

  const sendCommand = (lift: LiftInterface | undefined, floor: BankFloorInterface) => {
    // Update Redux selected lift+bank so usePostCommand picks them up.
    dispatch(updateBankSelected(bank as any));
    if (lift) {
      dispatch(updateLiftSelected(lift as any));
    }
    triggerPostCommand({
      floor: floor.floorIndex,
      command: spec.controlFor(door),
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active: true,
    });
  };

  // Only suppress the panel entirely when there are NEITHER dynamic
  // actions NOR an MCE bank — operators of non-MCE deployments that
  // have action_bank rows configured should still see them.
  if (!isMce && !dynamicActionsAvailable) return null;
  if (sortedFloors.length === 0 && !dynamicActionsAvailable) {
    return (
      <div
        className={`monitoring__card__${theme} generalStyles__width100`}
        style={{ marginTop: 20, padding: 20 }}
      >
        <Empty description={<span>{t("general.bankHasNoFrontRearDoors")}</span>} />
      </div>
    );
  }

  return (
    <div
      className={`monitoring__card__${theme} generalStyles__width100`}
      style={{ marginTop: 20, padding: 20 }}
    >
      {dynamicActionsAvailable && (
        <>
          <div className="generalStyles__spaceBetween" style={{ marginBottom: 12 }}>
            <Tag color="geekblue">Dynamic Actions</Tag>
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              {String((bank as any).name ?? "")} · {actionBankRows.length} configured
            </span>
          </div>
          {ACTION_PANEL_ORDER.filter((type) => actionGroups[type].length > 0).map((type) => (
            <div key={type}>
              <ActionTypeSectionHeader type={type} count={actionGroups[type].length} />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                {actionGroups[type].map((row) => {
                  const primaryColor = row.color || undefined;
                  const action = (row as BankActionInterface & { action?: { cmd?: string } })
                    .action;
                  const cmdLabel = action?.cmd
                    ? `Control::${action.cmd}`
                    : `(no cmd resolved for action_bank ${row.id})`;
                  const isTogglePair = type === "TOGGLE" || type === "TOGGLETIMER";
                  return (
                    <div key={row.id} style={{ display: "flex", gap: 4 }}>
                      <Tooltip title={cmdLabel}>
                        <Button
                          size="small"
                          style={
                            primaryColor
                              ? { backgroundColor: primaryColor, borderColor: primaryColor }
                              : undefined
                          }
                          loading={isLoadingPostCommand}
                          onClick={() => sendActionBankCommand(row, true)}
                        >
                          {row.label || "Action"}
                        </Button>
                      </Tooltip>
                      {isTogglePair && row.toggleLabel && (
                        <Tooltip title={`Toggle off · ${row.toggleLabel}`}>
                          <Button
                            size="small"
                            style={
                              row.toggleColor
                                ? {
                                    backgroundColor: row.toggleColor,
                                    borderColor: row.toggleColor,
                                  }
                                : undefined
                            }
                            loading={isLoadingPostCommand}
                            onClick={() => sendActionBankCommand(row, false)}
                          >
                            {row.toggleLabel}
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {isMce && sortedFloors.length > 0 && <Divider style={{ margin: "16px 0" }} />}
        </>
      )}
      {isMce && sortedFloors.length > 0 && (
        <>
          <div className="generalStyles__spaceBetween" style={{ marginBottom: 12 }}>
            <Tag color="blue">MCE Bank Commands</Tag>
            <span style={{ fontSize: 12, opacity: 0.7 }}>{String((bank as any).name ?? "")}</span>
          </div>

          <div style={{ marginBottom: 8 }}>
            <Radio.Group
              value={commandKind}
              onChange={(e) => setCommandKind(e.target.value)}
              size="small"
            >
              {COMMAND_SPECS.map((s) => (
                <Radio.Button key={s.key} value={s.key}>
                  {s.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>

          <div style={{ marginBottom: 12 }}>
            <Radio.Group value={door} onChange={(e) => setDoor(e.target.value)} size="small">
              <Radio.Button value="FRONT" disabled={!hasFront}>
                {t("general.frontDoors")}
              </Radio.Button>
              <Radio.Button value="REAR" disabled={!hasRear}>
                {t("general.rearDoors")}
              </Radio.Button>
            </Radio.Group>
          </div>

          {spec.perLift ? (
            <div>
              {lifts.length === 0 ? (
                <Empty description="Bank has no lifts" />
              ) : (
                lifts.map((lift) => (
                  <div
                    key={String((lift as any).id ?? lift.position)}
                    className={`drawer__box__control__panel__${theme}`}
                    style={{ padding: 8, marginBottom: 8 }}
                  >
                    <div style={{ marginBottom: 6, fontSize: 12 }}>
                      <Tag color="cyan">{String(lift.name ?? `Car ${lift.position}`)}</Tag>
                    </div>
                    <FloorButtonGrid
                      floors={sortedFloors}
                      isLoading={isLoadingPostCommand}
                      isEnabled={(floor) => liftHasDoor(lift, floor, door)}
                      tooltip={(floor, enabled) =>
                        enabled
                          ? `${spec.label} ${door === "FRONT" ? "front" : "rear"} • ${
                              floor.name || `Floor ${floor.floorIndex}`
                            }`
                          : `No ${door.toLowerCase()} door on this floor for ${
                              lift.name ?? `Car ${lift.position}`
                            }`
                      }
                      onClick={(floor) => sendCommand(lift, floor)}
                    />
                  </div>
                ))
              )}
            </div>
          ) : (
            // Hall calls are bank-level (the lift_id field is still required by
            // the Redux command body, so we use position-1 as the canonical lift).
            <div className={`drawer__box__control__panel__${theme}`} style={{ padding: 8 }}>
              <div style={{ marginBottom: 6, fontSize: 12 }}>
                <Tag color="purple">All Lifts</Tag>
              </div>
              <FloorButtonGrid
                floors={sortedFloors}
                isLoading={isLoadingPostCommand}
                isEnabled={(floor) => (door === "FRONT" ? !!floor.frontDoor : !!floor.rearDoor)}
                tooltip={(floor, enabled) =>
                  enabled
                    ? `${spec.label} ${door === "FRONT" ? "front" : "rear"} • ${
                        floor.name || `Floor ${floor.floorIndex}`
                      }`
                    : `No ${door.toLowerCase()} door on floor ${floor.floorIndex}`
                }
                onClick={(floor) => sendCommand(lifts[0], floor)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Re-exported from ./terminalCommandSpecs, where these now live. Kept here so
// existing importers (tests, useFrontRearDoorAvailability) keep resolving.
export { COMMAND_SPECS, liftHasDoor, bankHasDoorSide };
export type { CommandKind, DoorSide };
